import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics, trackPageView } from '../services/analytics';

/**
 * Custom hook for automatic page tracking with React Router
 * This hook automatically tracks page views when the route changes
 */
export const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    if (analytics.isAnalyticsEnabled()) {
      // Get page title from document or generate from pathname
      const pageTitle = document.title || getPageTitleFromPath(location.pathname);
      
      trackPageView({
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        custom_parameters: {
          route_name: getRouteNameFromPath(location.pathname),
          timestamp: new Date().toISOString()
        }
      });
    }
  }, [location]);

  return {
    isEnabled: analytics.isAnalyticsEnabled(),
    status: analytics.getStatus()
  };
};

/**
 * Generate a readable page title from the pathname
 */
function getPageTitleFromPath(pathname: string): string {
  const pathMap: Record<string, string> = {
    '/': 'Home - SarvaLok',
    '/chat': 'Chat - SarvaLok',
    '/login': 'Login - SarvaLok',
    '/sign-up': 'Sign Up - SarvaLok',
    '/pricing': 'Pricing - SarvaLok',
    '/contact-us': 'Contact Us - SarvaLok',
    '/privacy-policy': 'Privacy Policy - SarvaLok',
    '/terms-condition': 'Terms & Conditions - SarvaLok',
    '/settings': 'Settings - SarvaLok',
    '/features': 'Features - SarvaLok',
    '/for-education': 'For Education - SarvaLok',
    '/for-organisation': 'For Organisations - SarvaLok',
    '/for-government': 'For Government - SarvaLok',
    '/for-investor': 'For Investors - SarvaLok',
    '/documentation': 'Documentation - SarvaLok',
    '/use-cases': 'Use Cases - SarvaLok',
    '/security': 'Security - SarvaLok',
    '/enterprise': 'Enterprise - SarvaLok',
    '/about-us': 'About Us - SarvaLok',
    '/why-sarvalok': 'Why SarvaLok - SarvaLok',
    '/plan-details': 'Plan Details - SarvaLok',
    '/pricing-policy': 'Pricing Policy - SarvaLok',
    '/reset-password': 'Reset Password - SarvaLok',
    '/404': '404 - Page Not Found - SarvaLok',
    '/503': '503 - Service Unavailable - SarvaLok'
  };

  return pathMap[pathname] || `${pathname.replace('/', '').replace('-', ' ')} - SarvaLok`;
}

/**
 * Generate a route name for analytics categorization
 */
function getRouteNameFromPath(pathname: string): string {
  const routeMap: Record<string, string> = {
    '/': 'home',
    '/chat': 'chat',
    '/login': 'auth',
    '/sign-up': 'auth',
    '/pricing': 'pricing',
    '/contact-us': 'contact',
    '/privacy-policy': 'legal',
    '/terms-condition': 'legal',
    '/settings': 'settings',
    '/features': 'features',
    '/for-education': 'solutions',
    '/for-organisation': 'solutions',
    '/for-government': 'solutions',
    '/for-investor': 'solutions',
    '/documentation': 'docs',
    '/use-cases': 'use-cases',
    '/security': 'security',
    '/enterprise': 'enterprise',
    '/about-us': 'about',
    '/why-sarvalok': 'about',
    '/plan-details': 'pricing',
    '/pricing-policy': 'legal',
    '/reset-password': 'auth',
    '/404': 'error',
    '/503': 'error'
  };

  return routeMap[pathname] || 'unknown';
}

export default useAnalytics;